import { Fragment } from 'react';

import { makeStyles } from '@material-ui/core';

import {
  ActivityChangeOptions,
  ActivityItem,
} from '@spotify/plugin-confidence-activity-react';
import { createWorkflowUpdatesChangeExtension } from '@spotify/plugin-confidence-workflows-react';

const useStyles = makeStyles({
  addedText: {
    color: 'var(--bui-fg-success)',
  },
  removedText: {
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'line-through',
  },
});

interface Metric {
  name?: string;
  [key: string]: unknown;
}

interface MetricsData {
  metrics?: Metric[];
  [key: string]: unknown;
}

const MetricsChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();

  const oldValue = change.oldValue as MetricsData | null;
  const newValue = change.newValue as MetricsData | null;

  const oldMetrics = oldValue?.metrics ?? [];
  const newMetrics = newValue?.metrics ?? [];

  const oldMetricNames = new Set(oldMetrics.map(m => m.name || ''));
  const newMetricNames = new Set(newMetrics.map(m => m.name || ''));

  const addedMetrics = newMetrics.filter(
    m => !oldMetricNames.has(m.name || ''),
  );
  const removedMetrics = oldMetrics.filter(
    m => !newMetricNames.has(m.name || ''),
  );

  return (
    <Fragment>
      {addedMetrics.length > 0 && (
        <ActivityItem.Section label="Added metrics">
          {addedMetrics.map((metric, index) => (
            <div key={index}>
              <span className={classes.addedText}>{metric.name}</span>
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {removedMetrics.length > 0 && (
        <ActivityItem.Section label="Removed metrics">
          {removedMetrics.map((metric, index) => (
            <div key={index}>
              <span className={classes.removedText}>{metric.name}</span>
            </div>
          ))}
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export const METRICS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'metrics-change',
  predicate: ({ change }) => change.fieldPath === '/module_data/metrics',
  title: change => {
    const oldValue = change.oldValue as MetricsData | null;
    const newValue = change.newValue as MetricsData | null;

    const oldMetrics = oldValue?.metrics ?? [];
    const newMetrics = newValue?.metrics ?? [];

    if (newMetrics.length > oldMetrics.length) {
      const addedCount = newMetrics.length - oldMetrics.length;
      return `Added ${addedCount} metric${addedCount > 1 ? 's' : ''}`;
    }

    if (oldMetrics.length > newMetrics.length) {
      const removedCount = oldMetrics.length - newMetrics.length;
      return `Removed ${removedCount} metric${removedCount > 1 ? 's' : ''}`;
    }

    return 'Updated metrics configuration';
  },
  render: MetricsChangeRender,
});
